import { useState, useEffect } from 'react';
import BulkImport from './components/BulkImport';

import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Building,
  MapPin,
  DollarSign,
  Bed,
  Bath
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function PropertiesManagement() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const allSelected = properties.length > 0 && selected.size === properties.length;
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(properties.map(p => p.id)));
  };
  const toggleSelect = (id) => {
    setSelected(prev => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id); else copy.add(id);
      return copy;
    });
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} properties?`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selected].map(id => api.delete(`/properties/${id}`)));
      setProperties(prev => prev.filter(p => !selected.has(p.id)));
      setSelected(new Set());
    } catch (e) {
      console.error('Bulk delete failed', e);
      alert('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };
  const toggleStatus = async (p) => {
    const newStatus = p.status === 'available' ? 'sold' : 'available';
    try {
      const res = await api.put(`/properties/${p.id}`, { status: newStatus });
      setProperties(prev => prev.map(x => x.id === p.id ? { ...x, status: newStatus } : x));
    } catch (e) {
      console.error('Failed to update status', e);
      alert('Failed to update status');
    }
  };


  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const res = await api.get('/properties');
      setProperties(res.data || []);
    } catch (e) {
      console.error('Failed to fetch properties', e);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async (onlySelected=false) => {
    try {
      const ids = onlySelected ? [...selected].join(',') : '';
      const url = ids ? `/properties/export?selected_ids=${encodeURIComponent(ids)}` : '/properties/export';
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `properties-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error('Failed to export CSV', e);
      alert('Failed to export CSV');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/properties/${deleteId}`);
      setProperties(properties.filter(p => p.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Failed to delete property:', error);
      alert('Failed to delete property');
    }
  };

  const filteredProperties = properties.filter(property =>
    property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
          <Button variant="outline" onClick={bulkDelete} disabled={selected.size===0 || bulkDeleting}>
            {bulkDeleting ? 'Deleting...' : `Delete Selected (${selected.size})`}
          </Button>
          <Button variant="outline" onClick={() => exportCsv(false)}>Export CSV (All)</Button>
          <Button variant="outline" onClick={() => exportCsv(true)} disabled={selected.size===0}>Export CSV (Selected)</Button>
          <BulkImport onImported={fetchProperties} />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="properties-title">Properties</h1>
          <p className="text-muted-foreground">Manage your property listings</p>
        </div>
        <Button asChild data-testid="add-property-button">
          <Link to="/dashboard/properties/create">
            <Plus className="mr-2 h-4 w-4" />
            Add Property
          </Link>
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-properties-input"
          />
        </div>
      </div>

      {/* Properties Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-lg text-gray-500">Loading properties...</div>
        </div>
      ) : filteredProperties.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No properties found</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first property</p>
            <Button asChild>
              <Link to="/dashboard/properties/create">
                <Plus className="mr-2 h-4 w-4" />
                Add Property
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-grid">
          {filteredProperties.map((property) => (
            <Card key={property.id} className="overflow-hidden" data-testid={`property-card-${property.id}`}>
              <div className="absolute z-10 p-2">
                <input type="checkbox" checked={selected.has(property.id)} onChange={() => toggleSelect(property.id)} />
              </div>
              <div className="aspect-video bg-gray-200 relative">
                {property.featured_image ? (
                  <img
                    src={property.featured_image}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                ) : property.images && property.images.length > 0 ? (
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Building className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    property.status === 'available' ? 'bg-green-100 text-green-800' :
                    property.status === 'sold' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {property.status}
                  </span>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-lg">{property.title}</CardTitle>
                <CardDescription className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {property.location}
                </CardDescription>
              </CardHeader>
                {/* Overlays inside image area */}
                <div className="absolute bottom-2 left-2 bg-white/90 rounded px-2 py-1 flex items-center gap-2 shadow">
                  <span className="text-xs text-gray-700">Featured:</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${property.is_featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                    {property.is_featured ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="absolute bottom-2 right-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => toggleStatus(property)}
                    className="bg-white/90 hover:bg-white"
                  >
                    {property.status === 'available' ? 'Mark Sold' : 'Mark Available'}
                  </Button>
                </div>

              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-2xl font-bold text-blue-600">
                    <DollarSign className="h-5 w-5" />
                    {property.price.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Bed className="h-4 w-4" />
                      {property.bedrooms} beds
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath className="h-4 w-4" />
                      {property.bathrooms} baths
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {property.square_feet.toLocaleString()} sq ft
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" asChild className="flex-1">
                      <Link to={`/dashboard/properties/${property.id}/edit`}>
                        <Edit className="mr-2 h-3 w-3" />
                        Edit
                      </Link>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 text-red-600 hover:text-red-700"
                      onClick={() => setDeleteId(property.id)}
                    >
                      <Trash2 className="mr-2 h-3 w-3" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the property.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
